// src/components/cctv/CameraView.js
import React, { useEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";

const CameraView = ({
    onBack,
    streamUrl,
    useLegacyPlayer = false,
    usePostEmbed = false,
    embedUrl,
    embedParams,
    hybridAuth = { enabled: false },
}) => {
    console.log("st", streamUrl)

    const isM3U8 = useLegacyPlayer && streamUrl?.includes(".m3u8");

    const formRef = useRef(null);
    const iframeRef = useRef(null);

    // Auto-submit hidden form whenever we have valid embed params
    useEffect(() => {
        if (useLegacyPlayer) return;
        if (
            !usePostEmbed ||
            !embedUrl ||
            !embedParams?.id1 ||
            !embedParams?.id2 ||
            !embedParams?.hash ||
            (hybridAuth?.enabled && hybridAuth?.hasError !== true)
        ) {
            return;
        }

        try {
            if (formRef.current) {
                formRef.current.submit();
            }
        } catch (e) {
            console.error("[CameraView] Failed to submit CCTV embed form", e);
        }
    }, [
        usePostEmbed,
        embedUrl,
        embedParams?.id1,
        embedParams?.id2,
        embedParams?.hash,
        hybridAuth?.enabled,
        hybridAuth?.hasError,
    ]);

    const renderPlayer = () => {
        if (isM3U8) {
            return (
                <video
                    src={streamUrl}
                    autoPlay
                    controls
                    playsInline
                    className="w-full h-full object-contain"
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.src) {
            return (
                <iframe
                    title="cctv-hybrid"
                    src={hybridAuth.src}
                    className="w-full h-full"
                    allowFullScreen
                    frameBorder="0"
                />
            );
        }

        if (useLegacyPlayer) {
            return (
                <iframe
                    title="cctv"
                    src={streamUrl}
                    className="w-full h-full"
                    allowFullScreen
                    frameBorder="0"
                    referrerpolicy="no-referrer-when-downgrade"
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.hasError !== true && !hybridAuth?.src) {
            return <div className="text-white">Loading camera...</div>;
        }

        if (
            usePostEmbed &&
            embedUrl &&
            embedParams?.id1 &&
            embedParams?.id2 &&
            embedParams?.hash
        ) {
            return (
                <div className="w-full h-full">
                    <iframe
                        name="center-embed"
                        id="center-embed"
                        ref={iframeRef}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allowFullScreen
                        className="w-full h-full"
                        title="cctv-embed"
                    />

                    <form
                        ref={formRef}
                        method="POST"
                        action={embedUrl}
                        target="center-embed"
                        style={{ display: "none" }}
                    >
                        <input type="hidden" name="id1" value={embedParams.id1} />
                        <input type="hidden" name="id2" value={embedParams.id2} />
                        <input type="hidden" name="hash" value={embedParams.hash} />
                    </form>
                </div>
            );
        }

        return (
            <iframe
                title="cctv"
                src={streamUrl}
                className="w-full h-full"
                allowFullScreen
                frameBorder="0"
            />
        );
    };

    const handleBack = (e) => {
        // Stop the full-screen iframe from swallowing / re-targeting the tap.
        e.preventDefault();
        e.stopPropagation();
        if (typeof onBack === "function") onBack();
    };

    return (
        <div className="fixed inset-0 bg-black z-50">
            {/* PLAYER — rendered first so the back control can stack above the iframe */}
            <div className="flex h-full items-center justify-center bg-black overflow-hidden">
                <div className="w-full h-full">
                    {renderPlayer()}
                </div>
            </div>

            {/*
              Keep the back control AFTER the iframe in the DOM with a high z-index.
              On iOS WKWebView, a full-screen cross-origin iframe often steals the first
              tap when the button is painted underneath it — which feels like needing
              two taps to go back.
            */}
            <button
                type="button"
                onClick={handleBack}
                className="absolute top-4 left-4 z-[100] flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm pointer-events-auto"
                aria-label="Go back"
            >
                <ChevronLeft className="w-6 h-6" />
            </button>
        </div>
    );
};

export default CameraView;
